import { useState } from 'react';
import { Alert, Button, Skeleton, Tooltip } from 'antd';
import { decimal, isZero, money, tone } from './format';
import { LotsDrawer } from './LotsDrawer';
import type { AccountBasisView, CostBasisAudience, LotsSource } from './types';

type Holding = { symbol?: string | null; cost_basis_metadata?: unknown };
type Metadata = {
  amount?: string | null;
  currency?: string | null;
  basis_as_of?: string | null;
  provider?: string | null;
  completeness?: string;
  reasons?: string[];
};
function metadata(value: unknown): Metadata | undefined {
  if (!value || typeof value !== 'object') return;
  const raw = value as Record<string, unknown>;
  if (
    typeof raw.amount !== 'string' ||
    !/^-?\d+(\.\d+)?$/.test(raw.amount) ||
    raw.amount.length > 4096
  )
    return;
  if (raw.provider !== 'APEX' && raw.provider !== 'PLAID') return;
  if (typeof raw.currency !== 'string' || !/^[A-Z]{3}$/.test(raw.currency)) return;
  return {
    amount: raw.amount,
    currency: raw.currency,
    provider: raw.provider,
    basis_as_of: typeof raw.basis_as_of === 'string' ? raw.basis_as_of : null,
    completeness: typeof raw.completeness === 'string' ? raw.completeness : undefined,
  };
}

/**
 * Cost basis or unrealized gain for one holding row. `assetId` is the holding's
 * Apex asset ID, resolved by the portal; a row without one never matches a
 * security, and rows are never joined by ticker.
 */
export function CostBasisCell({
  basis,
  holding,
  assetId,
  lots,
  gain = false,
  external = false,
  audience = 'investor',
}: {
  basis: AccountBasisView;
  holding: Holding;
  assetId?: string | null;
  /** Lot reads for this account; omit to hide the lot drill-down. */
  lots?: LotsSource;
  gain?: boolean;
  external?: boolean;
  audience?: CostBasisAudience;
}) {
  const { enabled, state } = basis;
  const [open, setOpen] = useState(false);
  if (!enabled) return null;
  if (holding.symbol?.toUpperCase() === 'USD')
    return <span className="cost-basis-muted">{gain ? '—' : 'n/a'}</span>;
  const source = metadata(holding.cost_basis_metadata);
  const summary = external ? undefined : state?.summary;
  const matches =
    summary?.securities.filter((security) => Boolean(assetId) && security.asset_id === assetId) ||
    [];
  const security = matches.length === 1 ? matches[0] : undefined;
  const verified = summary?.metadata.calculation_contract_verified === true;
  const reasons = [...(summary?.metadata.reasons || []), ...(security?.reasons || [])];
  const unsupported = reasons.includes('UNSUPPORTED_INSTRUMENT');
  const verifying = summary && !verified;
  if (!external && (!state || state.status === 'loading'))
    return <Skeleton.Input active size="small" style={{ width: 80 }} />;
  if (verifying)
    return <span className="cost-basis-muted">—{!gain && <small>Verifying</small>}</span>;
  if (unsupported)
    return (
      <span className="cost-basis-muted">{gain ? '—' : 'Not available for this security'}</span>
    );
  const cost = security?.current_cost ?? source?.amount;
  const currency = security?.currency ?? source?.currency;
  const usesApexCost = security?.current_cost != null;
  const asOf = usesApexCost ? summary?.metadata.basis_as_of : source?.basis_as_of;
  const stale = usesApexCost && summary?.metadata.freshness === 'stale';
  if (gain) {
    const value = verified ? security?.unrealized_gain : null;
    return (
      <span className={tone(value)}>
        {money(value, currency, true)}
        {value != null ? (
          <small>
            {security?.unrealized_gain_percent != null
              ? `${decimal(security.unrealized_gain_percent)}%`
              : isZero(cost)
                ? 'n/a at $0 basis'
                : 'Percentage unavailable'}
          </small>
        ) : cost != null ? (
          <small>
            {reasons.includes('CURRENCY_MISMATCH') ? 'Different currency' : 'Not comparable'}
          </small>
        ) : null}
      </span>
    );
  }
  const canOpen =
    lots &&
    verified &&
    summary?.metadata.capabilities?.tax_lots &&
    security &&
    assetId &&
    state?.apexId &&
    summary.metadata.generation;
  return (
    <span className="cost-basis-cell">
      <Tooltip
        title={
          cost == null
            ? 'Cost basis has not been reported or verified for this holding.'
            : `Reported by ${usesApexCost ? 'Apex' : source?.provider || 'Apex'}${asOf ? ` · As of ${asOf}` : ''}`
        }
      >
        <span>{money(cost, currency)}</span>
      </Tooltip>
      <small className={stale ? 'cost-basis-stale' : undefined}>
        {stale
          ? `As of ${asOf || 'unknown'}`
          : security?.cost_per_share != null
            ? `${money(security.cost_per_share, currency)} / sh`
            : cost == null
              ? 'Not reported'
              : source?.completeness !== 'complete' && source
                ? 'Partial data'
                : asOf
                  ? `As of ${asOf}`
                  : ''}
      </small>
      {canOpen && (
        <Button
          type="link"
          size="small"
          aria-label={`View purchase lots for ${holding.symbol || 'security'}`}
          onClick={() => setOpen(true)}
        >
          Lots
        </Button>
      )}
      {open && canOpen && (
        <LotsDrawer
          source={lots!}
          accountId={state!.apexId!}
          assetId={assetId!}
          title={holding.symbol || 'Security'}
          counts={{ open: security!.open_lot_count, closed: security!.closed_lot_count }}
          position={{
            cost: money(security!.current_cost, currency),
            average: money(security!.cost_per_share, currency),
            gain: money(security!.unrealized_gain, currency, true),
            gainTone: tone(security!.unrealized_gain),
          }}
          generation={summary!.metadata.generation!}
          audience={audience}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

export function CostBasisSummary({
  basis,
  name,
  lots,
  audience = 'investor',
}: {
  basis: AccountBasisView;
  name: string;
  /** Lot reads for this account; omit to hide the lot drill-down. */
  lots?: LotsSource;
  audience?: CostBasisAudience;
}) {
  const { enabled, state, retry } = basis;
  const [open, setOpen] = useState(false);
  if (!enabled) return null;
  if (!state || state.status === 'loading')
    return (
      <section className="cost-basis-summary">
        <h3>Cost basis</h3>
        <Skeleton active paragraph={{ rows: 1 }} />
      </section>
    );
  if (state.status === 'error')
    return (
      <Alert
        type="warning"
        message="We couldn't load cost basis"
        description="Holdings are still shown. Cost columns show — until it loads."
        action={<Button onClick={retry}>Try again</Button>}
      />
    );
  if (state.status === 'unsupported')
    return <Alert type="info" message="Purchase lots are not supported by this institution" />;
  const summary = state.summary!;
  if (!summary.metadata.calculation_contract_verified)
    return (
      <Alert
        type="info"
        message="Cost basis is on its way"
        description={
          audience === 'advisor'
            ? "We're verifying cost data for this account. Holdings and balances are shown separately."
            : "We're verifying cost data for this account. Your holdings and balances are shown separately."
        }
      />
    );
  const canOpen =
    lots && summary.metadata.capabilities?.tax_lots && summary.metadata.generation && state.apexId;
  return (
    <section className="cost-basis-summary">
      <div className="cost-basis-heading">
        <h3>Cost basis</h3>
        <span>
          As of {summary.metadata.basis_as_of || 'unknown'} · values at market close · securities
          only, excluding cash
          {summary.metadata.freshness === 'stale' && (
            <span className="cost-basis-stale"> · Data may be out of date</span>
          )}
        </span>
      </div>
      {summary.metadata.completeness !== 'complete' && (
        <Alert type="info" message="Cost basis is not available yet" />
      )}
      {summary.totals.map((total, index) => (
        <div key={`${total.currency}-${index}`}>
          <div className="cost-basis-cards">
            <div>
              <small>Market value · {total.currency || 'Unknown currency'}</small>
              <strong>{money(total.market_value, total.currency)}</strong>
            </div>
            <div>
              <small>Total cost basis</small>
              <strong>{money(total.current_cost, total.currency)}</strong>
            </div>
            <div>
              <small>Unrealized gain</small>
              <strong className={tone(total.unrealized_gain)}>
                {money(total.unrealized_gain, total.currency, true)}
              </strong>
              <small className={tone(total.unrealized_gain)}>
                {total.unrealized_gain_percent != null
                  ? `${decimal(total.unrealized_gain_percent)}%`
                  : isZero(total.current_cost)
                    ? 'n/a at $0 basis'
                    : '—'}
              </small>
            </div>
            <div>
              <small>Realized gain · all available dates</small>
              <strong className={tone(total.realized_gain)}>
                {money(total.realized_gain, total.currency, true)}
              </strong>
              <small>As reported by Apex</small>
            </div>
          </div>
          {total.current_cost == null && (
            <Alert
              type="info"
              message="Total cost basis unavailable"
              description="Totals cover securities only and can't be calculated from the available data. Available per-holding values are shown below."
            />
          )}
        </div>
      ))}
      {canOpen && <Button onClick={() => setOpen(true)}>View purchase lots</Button>}
      {open && canOpen && (
        <LotsDrawer
          source={lots!}
          accountId={state.apexId!}
          title={name}
          description="All securities"
          generation={summary.metadata.generation!}
          audience={audience}
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
}

export function CostBasisLegend({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <p className="cost-basis-muted">
      Cost basis is reported at market close; dates vary by account. — means we don’t know this
      value yet. $0.00 means the reported cost basis is zero. Open an account below to see purchase
      lots.
    </p>
  ) : null;
}
